import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { useRecoilState, useSetRecoilState } from 'recoil';
import { AxiosService, EndpointT } from '../utils/axiosService';
import { accessTokenAtom, userAtom } from '../recoil/atoms';
import { RouteNameEnum } from '../enums/routeName';
import { PathBuilder } from '../utils/pathBuilder';

type DefaultResponseT = {
  data: any | null,
  error: Error | any | null,
};

type RequestParams = {
  params?: any | null;
  queryString?: any | null;
};

type ServiceHookT = {
  get: (requestParams?: RequestParams | null, options?: RequestOptions) => Promise<DefaultResponseT>
  post: (payload: any, requestParams?: RequestParams | null, options?: RequestOptions) => Promise<DefaultResponseT>
  patch: (payload: any, requestParams?: RequestParams | null, options?: RequestOptions) => Promise<DefaultResponseT>
  put: (payload: any, requestParams?: RequestParams | null, options?: RequestOptions) => Promise<DefaultResponseT>
  delete: (requestParams?: RequestParams | null, options?: RequestOptions) => Promise<DefaultResponseT>
};

/** Override request option */
type RequestOptions = {
  timeout?: number;
  endpoint?: EndpointT;
};

const useServices = (
  path: string | null = null,
): {
  loading: boolean,
  service: ServiceHookT
} => {
  const [loading, setLoading] = React.useState(false);
  const navigate = useNavigate();
  const [accessToken, setAccessToken] = useRecoilState(accessTokenAtom);
  const setUser = useSetRecoilState(userAtom);

  const handleRequest = async (requestFunction: any): Promise<DefaultResponseT> => {
    setLoading(true);
    if (accessToken) {
      const refreshedToken = await AxiosService.getRefreshToken(accessToken);
      setAccessToken(refreshedToken);
    }
    const { data, error } = await requestFunction;
    setLoading(false);
    if (error?.response?.status === 401) {
      setAccessToken(null);
      setUser(null);
      navigate(RouteNameEnum.auth.login);
    }
    return { data, error };
  };

  const pathErrorResponse: DefaultResponseT = { data: null, error: new Error('Invalid request path') };

  const service: ServiceHookT = {
    get: async (requestParams?: RequestParams | null, options?: RequestOptions) => {
      if (!path) {
        return pathErrorResponse;
      }
      let newPath = path;
      if (requestParams) {
        newPath = PathBuilder.buildPath(newPath, requestParams);
      }
      return handleRequest(
        AxiosService.getRequest({
          path: newPath,
          accessToken,
          timeout: options?.timeout || undefined,
          endpoint: options?.endpoint || undefined,
        }),
      );
    },
    post: async (payload: any, requestParams?: RequestParams | null, options?: RequestOptions) => {
      if (!path) {
        return pathErrorResponse;
      }
      let newPath = path;
      if (requestParams) {
        newPath = PathBuilder.buildPath(newPath, requestParams);
      }
      return handleRequest(AxiosService.postRequest({
        path: newPath,
        payload,
        accessToken,
        timeout: options?.timeout || undefined,
        endpoint: options?.endpoint || undefined,
      }));
    },
    patch: async (payload: any, requestParams?: RequestParams | null, options?: RequestOptions) => {
      if (!path) {
        return pathErrorResponse;
      }
      let newPath = path;
      if (requestParams) {
        newPath = PathBuilder.buildPath(newPath, requestParams);
      }
      return handleRequest(AxiosService.patchRequest({
        path: newPath,
        payload,
        accessToken,
        timeout: options?.timeout || undefined,
        endpoint: options?.endpoint || undefined,
      }));
    },
    put: async (payload: any, requestParams?: RequestParams | null, options?: RequestOptions) => {
      if (!path) {
        return pathErrorResponse;
      }
      let newPath = path;
      if (requestParams) {
        newPath = PathBuilder.buildPath(newPath, requestParams);
      }
      return handleRequest(AxiosService.putRequest({
        path: newPath,
        payload,
        accessToken,
        timeout: options?.timeout || undefined,
        endpoint: options?.endpoint || undefined,
      }));
    },
    delete: async (requestParams?: RequestParams | null, options?: RequestOptions) => {
      if (!path) {
        return pathErrorResponse;
      }
      let newPath = path;
      if (requestParams) {
        newPath = PathBuilder.buildPath(newPath, requestParams);
      }
      return handleRequest(AxiosService.deleteRequest({
        path: newPath,
        accessToken,
        timeout: options?.timeout || undefined,
        endpoint: options?.endpoint || undefined,
      }));
    },

  };

  return { loading, service };
};

export default useServices;
